import React from 'react'
import { Add } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import { SimpleValue } from '../FieldPills'

import {
  selectCustomFields,
  fetchCustomFields
} from '@SonarCX/slices/customFields'
import useStyles from './styles'

export type SimpleFieldDropdownProps = {
  limit?: number
  values: SimpleValue[]
  setValues: React.Dispatch<React.SetStateAction<SimpleValue[]>>
  type?: LocalCustomField['object_type']
}

const SimpleFieldDropdown: React.FC<SimpleFieldDropdownProps> = ({
  limit = 5,
  values,
  setValues,
  type = 'external-user'
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { customFields, loaded, loading } = useAppSelector(selectCustomFields)

  const [search, setSearch] = React.useState('')

  const atLimit = React.useMemo(
    () => values.length === limit,
    [values.length, limit]
  )

  const searchVal = React.useMemo(
    () => search.trim().toLocaleLowerCase(),
    [search]
  )

  const availableFields = React.useMemo(() => {
    // @ts-ignore
    const restrictedTypes: LocalCustomField['field_type'][] = ['table', 'flow']
    const matchingFields = customFields.filter(field => {
      const typeCorrect = field.object_type === type
      const notRestricted = !restrictedTypes.includes(field.field_type)

      if (type !== 'external-user') {
        return typeCorrect && notRestricted
      }

      // Disable duplicates for users
      return (
        typeCorrect &&
        notRestricted &&
        !values.some(v => v.field_id === field.id)
      )
    })

    if (searchVal.length === 0) {
      return matchingFields
    }

    return matchingFields.filter(field => {
      return field.title.toLocaleLowerCase().includes(searchVal)
    })
  }, [searchVal, customFields, type, values])

  const classes = useStyles()

  // Load fields
  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchCustomFields())
    }
  }, [loaded, loading, dispatch])

  const handleClick = React.useCallback(
    (field: LocalCustomField) => {
      if (atLimit) {
        return
      }

      if (type !== 'external-user') {
        setValues(old => [
          ...old,
          { field_id: field.id, value: '', logic: 'eq' }
        ])
        return
      }

      // Disable duplicates for users
      setValues(old => {
        if (old.some(v => v.field_id === field.id)) {
          return old
        }

        return [...old, { field_id: field.id, value: '', logic: 'eq' }]
      })
    },
    [atLimit, type, setValues]
  )

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <input
          autoFocus
          placeholder='Search...'
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={classes.input}
        />
      </div>

      <div className={classes.fields}>
        {availableFields.length === 0 &&
          (searchVal.length === 0 ? (
            <div className={classes.infoText}>No custom fields</div>
          ) : (
            <div className={classes.infoText}>No matching fields</div>
          ))}

        {atLimit && (
          <div className={classes.infoText} style={{ marginBottom: '0.75rem' }}>
            Only {limit} fields can be searched at once
          </div>
        )}

        {availableFields.map(field => (
          <div
            key={field.id}
            onClick={() => handleClick(field)}
            className={classes.field}
            style={{ cursor: atLimit ? 'default' : 'pointer' }}
          >
            <Add size={theme.size.xs} />

            <span>{field.title}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SimpleFieldDropdown
